import type { GraphicsBackend } from '@/graphics/GraphicsBackend'

/**
 * 工具拾取的 backend 策略与异步调度原语。
 *
 * 为什么需要这一层：
 * - WebGL2 的 GSplat 有同步 GPU 拾取（`pickSync`：渲染 ID pass + 同步 readPixels，1–3ms），
 *   同步入口最省延迟，因此 WebGL2 保持同步调用。
 * - WebGPU 没有同步 GPU 读回，同步入口会回退到 CPU 整份 splat 遍历（实测单次约 200ms，
 *   主线程占用 70% 以上）。因此 WebGPU 下交互式拾取必须走异步入口。
 * - 异步入口不能逐事件排队：GPU 拾取本身要跑一遍整份 splat 的顶点处理，排队只会互相抢 GPU。
 *   所以要「单在途 + 限频 + 过期结果丢弃」，交互上再配合缓存或几何近似让光标跟随。
 */

/** 当前 backend 是否应该优先使用异步拾取入口。 */
export function prefersAsyncToolPicking(graphics: GraphicsBackend | null | undefined): boolean {
  return graphics?.kind === 'webgpu'
}

/**
 * hover 型拾取的合并器。
 *
 * 语义：同一时刻最多一个在途请求；两次发起之间遵守最小间隔；通过 generation 令牌丢弃过期结果
 * （视图切换、工具停用、点击抢占等场景调用 `invalidate()`）。
 */
export class AsyncHoverPickCoalescer {
  private inFlight = false
  private generation = 0
  private lastStartedAt = Number.NEGATIVE_INFINITY

  constructor(private readonly minIntervalMs = 0) {}

  /** 是否有在途请求。 */
  get pending(): boolean {
    return this.inFlight
  }

  /** 作废在途请求的结果，并解除「在途」占用。 */
  invalidate(): void {
    this.generation += 1
    this.inFlight = false
    this.lastStartedAt = Number.NEGATIVE_INFINITY
  }

  /** 令牌是否仍然有效（结果回写前必须检查）。 */
  isCurrent(token: number): boolean {
    return token === this.generation
  }

  /**
   * 发起一次 hover 拾取。
   *
   * @param task - 实际拾取任务；参数为本轮令牌，任务内必须用 `isCurrent(token)` 校验
   * @param now - 当前时间戳，便于测试注入
   * @returns 是否真正发起；false 表示被在途或最小间隔挡住
   */
  schedule(task: (token: number) => Promise<void>, now = performance.now()): boolean {
    if (this.inFlight) {
      return false
    }
    if (now - this.lastStartedAt < this.minIntervalMs) {
      return false
    }

    this.lastStartedAt = now
    const token = ++this.generation
    this.inFlight = true
    void task(token)
      .catch(() => {
        // 单次 hover 拾取失败不应中断交互
      })
      .finally(() => {
        if (this.isCurrent(token)) {
          this.inFlight = false
        }
      })
    return true
  }
}

/**
 * 点击型拾取的串行队列。
 *
 * 点击必须严格保序（连续点击对应连续落点），因此用 promise 链串行执行；同样用令牌保证
 * 「测量完成/取消/工具停用」之后迟到的结果不会再生效。
 */
export class AsyncPickQueue {
  private chain: Promise<void> = Promise.resolve()
  private generation = 0
  private inFlight = 0

  /** 在途请求数量；调用方常用它临时暂停 hover 拾取，避免 hover 结果覆盖刚落下的点。 */
  get pending(): number {
    return this.inFlight
  }

  /** 作废所有尚未回写的点击结果。 */
  invalidate(): void {
    this.generation += 1
  }

  /** 令牌是否仍然有效。 */
  isCurrent(token: number): boolean {
    return token === this.generation
  }

  /** 入队一次点击拾取；任务按入队顺序串行执行。 */
  enqueue(task: (token: number) => Promise<void>): void {
    const token = this.generation
    this.inFlight += 1
    this.chain = this.chain
      .then(async () => {
        if (!this.isCurrent(token)) {
          return
        }
        await task(token)
      })
      .catch(() => {
        // 单次点击拾取失败按未命中处理
      })
      .finally(() => {
        this.inFlight = Math.max(0, this.inFlight - 1)
      })
  }
}
